// Version-agnostic entry point for the flow-extension runner.
//
// Consumers import { getServiceConfigs, runServiceMethod } from
// '@flowrunner/flow-extension-runner' without knowing the extension format version. This module requires
// the service module once (in the v1 ServerCode sandbox so v1 modules register), then branches on whether
// the module registered a v2 extension: a hit in the v2 registry ⇒ v2, otherwise v1.

import './namespace'

import * as v1 from './v1'
import { createFlowrunnerSandboxV1 } from './v1/namespace'
import * as v2 from './v2'

export * from './errors'
export type { RunServiceMethodData } from './v2'

// The file-storage contract and its value shapes, so a host can build its own `ctx.files` for
// `runServiceMethod({ createFiles })` — see FilesApi in ./common/context-files.
export type { FlowrunnerContextOptions } from './common/context'
export type {
  DeleteOptions,
  DeleteResult,
  FileInfo,
  FilesApi,
  GetOptions,
  ListOptions,
  ListResult,
  UploadFileOptions,
  UploadResult,
  UsageResult,
} from './common/context-files'
export { FileScope } from './common/context-files'

export type ExtensionVersion = 'v1' | 'v2'

export {
  createFlowrunnerSandboxV1,
}

// Require the module through the v1 sandbox (harmless for v2 — it just runs createExtension), then detect.
// The require is cached, so the subsequent v1/v2 call re-requires for free.
export function detectVersion(serviceId: string, serviceEntryPointPath: string): ExtensionVersion {
  createFlowrunnerSandboxV1(serviceId, () => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    require(serviceEntryPointPath)
  })

  return v2.getExtension(serviceId) ? 'v2' : 'v1'
}

export function getServiceConfigs(
  serviceId: string,
  serviceEntryPointPath: string
): ReturnType<typeof v1.getServiceConfigs> {
  return detectVersion(serviceId, serviceEntryPointPath) === 'v2'
    ? v2.getServiceConfigs(serviceId, serviceEntryPointPath)
    : v1.getServiceConfigs(serviceId, serviceEntryPointPath)
}

export async function runServiceMethod(data: v2.RunServiceMethodData): Promise<unknown> {
  const frameworkVersion = detectVersion(data.serviceId, data.serviceEntryPointPath)

  const result = frameworkVersion === 'v2'
    ? await v2.runServiceMethod(data)
    : await v1.runServiceMethod(data as Parameters<typeof v1.runServiceMethod>[0])

  // console.log('got result:', JSON.stringify({
  //   frameworkVersion,
  //   serviceId: data.serviceId,
  //   methodName: data.methodName,
  //   methodData: data.methodData,
  //   methodArgs: data.methodArgs,
  //   result,
  // }, null, 2))

  return result
}

// Build the SharedExtensionDefinition model (serviceInfo + methodsMap). v2 derives it from the declarative
// definition; v1 services are modeled by the jsdoc-parser, so this only serves v2 (returns null for v1).
export function buildServiceDefinition(
  serviceId: string,
  serviceEntryPointPath: string
): v2.SharedExtensionDefinition | null {
  return detectVersion(serviceId, serviceEntryPointPath) === 'v2'
    ? v2.buildServiceDefinition(serviceId, serviceEntryPointPath)
    : null
}
